import { Link } from "@material-ui/core";
import { Stripe, StripeElements } from "@stripe/stripe-js";
import React, { useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import styled from "styled-components";

import { FlexLayout } from "js/Components/FlexLayout";
import { TextContent } from "js/Components/TextContent";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { useExperiments } from "js/react/hooks/useExperiments";
import { useScreenSize } from "js/react/hooks/useScreenSize";
import { AiOnboardingSteps } from "js/react/views/AiOnboardingFlow/AiOnboardingFlowConstants";
import FlowProgress from "js/react/views/AiOnboardingFlow/components/FlowProgress/FlowProgress";

import { AddressForm } from "../../components/AddressForm/AddressForm";
import { PaymentForm } from "../../components/PaymentForm/PaymentForm";
import { PlanFeatures } from "../../components/PlanFeatures/PlanFeatures";
import { PlanSummary } from "../../components/PlanSummary/PlanSummary";
import { PromoCoupon, PromoForm } from "../../components/PromoForm/PromoForm";
import { SubmissionButtons } from "../../components/SubmissionButtons/SubmissionButtons";
import { TermsOfUseAndRefundPolicy } from "../../components/TermsOfUseAndRefundPolicy/TermsOfUseAndRefundPolicy";
import { LoadingState } from "../components/LoadingState";

import withElements from "../../../withElements";
import withStripe from "../../../withStripe";

import { type ProCheckoutState, ProCheckoutButtonsContainer, ProCheckoutContainer, ProCheckoutContentContainer } from "./ProCheckout";

const Container = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100vh;
    height: 100dvh;
    background: #FFF;
    overflow: hidden;
    position: relative;
`;

const StickyHeader = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    display: flex;
    padding: 30px 30px 10px;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 5px;
    flex-shrink: 0;
    background: #FFF;
    z-index: 10;
`;

const ScrollContent = styled.div`
    position: absolute;
    top: 120px;
    left: 0;
    right: 0;
    bottom: 0;
    overflow-y: auto;
    overflow-x: hidden;
    display: flex;
    padding: 0px 30px 150px;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    gap: 20px;
`;

const StickyBottom = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    padding: 20px 30px;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    flex-shrink: 0;
    background: #F7FAFC;
    box-shadow: 0 -2px 25px 0 rgba(0, 0, 0, 0.08);
    z-index: 10;
`;

const OverlayContainer = styled.div<{ isCheckoutProVariant?: boolean, useMobileLayout?: boolean, useFullScreen?: boolean }>`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border-radius: 0;
    background: #FFF;
    z-index: 1000;
    box-shadow: none;
    overflow: hidden;

    height: ${({ isCheckoutProVariant }) => isCheckoutProVariant ? "76vh" : "90vh"};
    width: ${({ useMobileLayout }) => useMobileLayout ? "600px" : "825px"};

    
    border-radius: 4px;
    box-shadow: 0px 9px 37px 0px rgba(0, 0, 0, 0.2);

    ${({ useMobileLayout }) => useMobileLayout && `
        height: 90vh;
        width: 600px;
    `}
    ${({ useFullScreen }) => useFullScreen && `
        height: 100vh;
        height: 100dvh;
        width: 100vw;
    `}
`;

const CheckoutContentContainer = styled.div`
    align-self: stretch;
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;
    padding-right: 0;

    .dialog-content {
        padding: 0 34px;
        display: flex;
        flex-direction: column;
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        min-height: 0;
    }
`;

const FlowProgressContainer = styled.div`
    width: 100%;
    flex-shrink: 0;
    position: sticky;
    bottom: 0;
    background: #FFF;
    z-index: 10;
`;

const StyledLink = styled(Link)`
    &&& {
        text-decoration: underline;
        color: #11a9e2;
        cursor: pointer;
    }
`;

export const ProCheckoutContent = withStripe(withElements(({
    isTrial,
    isAnnual,
    isUpgradeMode,
    isSubmitting,
    canSubmit,
    showProgressLoader,
    paymentMethodCreationError,
    billingAddress,
    billingInterval,
    productDisplayName,
    prices,
    promo,
    availableReferralCredit,
    handleBillingIntervalChange,
    handlePaymentElementChange,
    handleBillingAddressChange,
    handleVerifyPromoCode,
    handleSubmitPayment,
    handleCancel,
    stripe,
    elements
}: ProCheckoutState & { stripe: Stripe, elements: StripeElements }) => {
    const { t } = useTranslation();
    const { isMobile, width: windowWidth } = useScreenSize();
    const useMobileLayout = isMobile || windowWidth < 916;
    const submitRef = useRef<any>(null);

    if (isSubmitting && showProgressLoader) {
        return (<LoadingState
            title={isTrial ? t("Starting free trial") : t("Upgrading plan")}
            subtitle={t("This will only take a moment...")}
        />);
    }

    const annualCostAtMonthlyRate = prices.month.amount * 12 / 100;
    const annualCostAtAnnualRate = prices.year.amount / 100;
    const annualSavingsPercentage = (1 - annualCostAtAnnualRate / annualCostAtMonthlyRate) * 100;

    const titleText = isTrial ? t("Start your 14 day free trial") : t("Upgrade to Beautiful.ai {{productDisplayName}}.", { productDisplayName });
    const subtitleText = isTrial ? t("Cancel before your trial ends and you won't be charged.") : t("Sorry, you are not eligible for a 14-day free trial because you have already had a trial in the past.");

    const submitLabel = isTrial ? t("Start Free Trial") : isUpgradeMode ? t("Upgrade Now") : t("Confirm");


    if (useMobileLayout) {
        return (
            <Container>
                <FetchingClickShield visible={isSubmitting} backgroundColor="white" />

                <StickyHeader>
                    <TextContent dialogTitle center>{titleText}</TextContent>
                    <TextContent dialogSubtitle center>
                        {subtitleText}
                    </TextContent>
                </StickyHeader>

                <ScrollContent>
                    <PaymentForm
                        variant="B"
                        paymentMethodCreationError={paymentMethodCreationError}
                        handlePaymentElementChange={handlePaymentElementChange}
                    />
                    <AddressForm
                        variant="B"
                        initialAddress={billingAddress}
                        onChange={handleBillingAddressChange}
                    />
                    {!promo ? <PromoForm variant="B" verifyPromoCode={handleVerifyPromoCode} /> : <PromoCoupon variant="B" coupon={promo.coupon} />}
                    <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                        <PlanFeatures useNarrowFormat={true} isAnnual={isAnnual} isTrial={isTrial} />
                    </div>
                </ScrollContent>

                <StickyBottom>
                    <PlanSummary
                        variant="B"
                        expandable
                        defaultExpanded={false}
                        isTrial={isTrial}
                        billingInterval={billingInterval}
                        productDisplayName={productDisplayName}
                        prices={prices}
                        promo={promo}
                        availableReferralCredit={availableReferralCredit}
                        handleBillingIntervalChange={handleBillingIntervalChange}
                        handleVerifyPromoCode={handleVerifyPromoCode}
                    />

                    <SubmissionButtons
                        submitLabel={submitLabel}
                        onSubmit={() => handleSubmitPayment(stripe, elements)}
                        disabled={!canSubmit}
                        onCancel={isUpgradeMode ? handleCancel : undefined}
                    />

                    <TextContent disclaimer center style={{ marginTop: -10, marginBottom: -10 }}>
                        {isAnnual && <>
                            <Trans
                                i18nKey="Pay ${{monthly}}/mo ({{annual}}/yr) when you <link>switch to monthly billing</link>. By continuing you agree to our <terms />"
                                values={{ monthly: prices.month.amount / 100, annual: annualCostAtMonthlyRate }}
                                components={{
                                    link: <StyledLink onClick={() => handleBillingIntervalChange("month")} />,
                                    terms: <TermsOfUseAndRefundPolicy textDecoration="none" />
                                }}
                            />
                        </>}
                        {!isAnnual && <>
                            <Trans
                                i18nKey="Save {{savings}}% when you <link>switch to annual billing</link>. By continuing you agree to our <terms />"
                                values={{ savings: annualSavingsPercentage.toFixed(0) }}
                                components={{
                                    link: <StyledLink onClick={() => handleBillingIntervalChange("year")} />,
                                    terms: <TermsOfUseAndRefundPolicy textDecoration="none" />
                                }}
                            />

                        </>}
                    </TextContent>
                </StickyBottom>
            </Container>
        );
    }

    return (
        <FlexLayout justify="center" align="flex-start" width="fill" height="fill" padding="20px 20px 0" gap={20}>
            <ProCheckoutContainer style={{ padding: `15px 30px` }}>
                <FlexLayout direction="column" gap={5}>
                    <TextContent dialogTitle>{titleText}</TextContent>
                    <TextContent dialogSubtitle>
                        {subtitleText}
                    </TextContent>
                </FlexLayout>

                <ProCheckoutContentContainer style={{ gap: 15 }}>
                    <PaymentForm
                        variant="B"
                        paymentMethodCreationError={paymentMethodCreationError}
                        handlePaymentElementChange={handlePaymentElementChange}
                    />
                    <AddressForm
                        variant="B"
                        initialAddress={billingAddress}
                        onChange={handleBillingAddressChange}
                        nextFieldRef={submitRef}
                    />
                    <TextContent disclaimer>
                        {t("By providing your card information, you allow Beautiful.ai to charge your card for future payments in accordance with their terms.")}
                    </TextContent>
                </ProCheckoutContentContainer>

                <ProCheckoutButtonsContainer>
                    <SubmissionButtons
                        ref={submitRef}
                        submitLabel={submitLabel}
                        onSubmit={() => handleSubmitPayment(stripe, elements)}
                        disabled={!canSubmit}
                        onCancel={isUpgradeMode ? handleCancel : undefined}
                    />

                    <TextContent disclaimer center>
                        <TermsOfUseAndRefundPolicy size="small" />
                    </TextContent>
                    <TextContent disclaimer center style={{ marginBottom: -10 }}>
                        {isAnnual && <>
                            <Trans
                                i18nKey="Pay ${{monthly}}/mo ({{annual}}/yr) when you <link>switch to monthly billing</link>."
                                values={{ monthly: prices.month.amount / 100, annual: annualCostAtMonthlyRate }}
                                components={{ link: <StyledLink onClick={() => handleBillingIntervalChange("month")} /> }}
                            />
                        </>}
                        {!isAnnual && <>
                            <Trans
                                i18nKey="Save {{savings}}% when you <link>switch to annual billing</link>."
                                values={{ savings: annualSavingsPercentage.toFixed(0) }}
                                components={{ link: <StyledLink onClick={() => handleBillingIntervalChange("year")} /> }}
                            />
                        </>}
                    </TextContent>
                </ProCheckoutButtonsContainer>
            </ProCheckoutContainer>

            <FlexLayout direction="column" gap={20} height="fill" scrollable="vertical" minWidth={280}>
                <PlanSummary
                    variant="B"
                    isTrial={isTrial}
                    billingInterval={billingInterval}
                    productDisplayName={productDisplayName}
                    prices={prices}
                    promo={promo}
                    availableReferralCredit={availableReferralCredit}
                    handleBillingIntervalChange={handleBillingIntervalChange}
                    handleVerifyPromoCode={handleVerifyPromoCode}
                />
                <PlanFeatures isAnnual={isAnnual} isTrial={isTrial} />
            </FlexLayout>
        </FlexLayout>
    );
}, { appearanceVariant: "B" }));

export const ProCheckoutContentWithContainer = ({ children }: { children: React.ReactNode }) => {
    const { isMobile, width: windowWidth } = useScreenSize();

    const { experiments: { checkout_pro_variant: isCheckoutProVariant }, loading: isLoadingExperiments } = useExperiments(["checkout_pro_variant"]);

    if (isLoadingExperiments) {
        return <FetchingClickShield visible backgroundColor="white" />;
    }

    const useMobileLayout = isMobile || windowWidth < 916;
    const useFullScreen = isMobile || windowWidth < 652;

    return (
        <OverlayContainer isCheckoutProVariant={isCheckoutProVariant} useMobileLayout={useMobileLayout} useFullScreen={useFullScreen}>
            <CheckoutContentContainer>
                {children}
            </CheckoutContentContainer>

            <FlowProgressContainer>
                <FlowProgress step={AiOnboardingSteps.CHECKOUT_OVERLAY} />
            </FlowProgressContainer>
        </OverlayContainer>
    );
};
